import { Injectable } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { de } from "date-fns/locale";

export interface SanitizeOptions {
  replaceTags?: string[];
  toHtml?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class SanitizeService {
  constructor(private sanitizer: DomSanitizer) {}

  /**
   * Sanitizes the provided HTML string according to the specified options.
   *
   * This method performs the following operations:
   * - If the "toHtml" option is true, it replaces newline characters with <br /> tags
   *   and adds CSS classes to all <a> tags so that links are styled appropriately.
   * - It then iterates over any tags specified in the "replaceTags" array and removes them.
   *   For <p> tags, it removes the tag itself but replaces the closing tag with a <br /> to maintain spacing.
   *   For other tags, it simply removes both the opening and closing tags.
   * - Finally, it bypasses Angular's built-in security to trust the resulting HTML, returning a SafeHtml object.
   *
   * @param html - The raw HTML string to sanitize.
   * @param params - Optional settings for sanitization:
   *   - toHtml: If true, performs newline and link formatting.
   *   - replaceTags: An array of tag names that should be removed from the HTML.
   * @returns A SafeHtml object containing the sanitized HTML.
   */
  getSafeHtml(html: string, params?: SanitizeOptions): SafeHtml {
    // If the "toHtml" option is set, adjust the HTML for display purposes.
    if (params?.toHtml) {
      // Replace newline characters with <br /> tags to preserve line breaks.
      html = html.replace(/\n/g, "<br />");
      // Add CSS classes to all <a> tags for link styling.
      html = html.replace(/<a /g, '<a class="text-blue-700 hover:underline" ');
    }

    // Process each tag specified in the "replaceTags" array.
    params?.replaceTags?.forEach((tag) => {
      switch (tag) {
        case "p":
          // For <p> tags, remove the opening tag entirely,
          // and replace the closing tag with a <br /> tag to maintain spacing.
          html = html.replace(/<p[^>]*>/g, "");
          html = html.replace(/<\/p>/g, "<br />");
          break;
        default:
          // For other tags, remove both the opening and closing tags.
          html = html.replace(new RegExp(`<${tag}[^>]*>`, "g"), "");
          html = html.replace(new RegExp(`</${tag}>`, "g"), "");
          break;
      }
    });

    // Bypass Angular's security to trust the processed HTML and return it as SafeHtml.
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
